import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { TableProps, Column } from './types';
import { styles, getRowStyles, getAlignClass, getSortableJustifyClass } from './styles';
import { Spinner } from '../Spinner';
import { Input } from '../Input';
import { formFieldStyles } from '@v2/styles/formFieldStyles';
import { layoutTokens } from '@v2/styles/layoutTokens';

const defaultMaxHeight = `calc(100vh - ${layoutTokens.tableOffset})`;

export function Table<T>({
  columns,
  data,
  keyExtractor,
  loading = false,
  error,
  emptyMessage = 'No data available',
  onRowClick,
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  className,
  maxHeight = defaultMaxHeight,
  sortConfig = null,
  onSortChange,
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filterSlot,
}: TableProps<T>): React.ReactElement {
  const hasToolbar = onSearchChange != null;
  const resolvedMaxHeight = maxHeight === false ? undefined : maxHeight;
  const [internalSelected, setInternalSelected] = useState<string[]>([]);

  const selected = selectedKeys.length > 0 || onSelectionChange ? selectedKeys : internalSelected;
  const setSelected = onSelectionChange || setInternalSelected;

  const isAllSelected = data.length > 0 && selected.length === data.length;
  const isSomeSelected = selected.length > 0 && selected.length < data.length;

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected(data.map(keyExtractor));
    }
  }, [isAllSelected, data, keyExtractor, setSelected]);

  const handleSelectRow = useCallback(
    (key: string) => {
      if (selected.includes(key)) {
        setSelected(selected.filter(k => k !== key));
      } else {
        setSelected([...selected, key]);
      }
    },
    [selected, setSelected],
  );

  const getCellValue = (row: T, column: Column<T>, index: number): React.ReactNode => {
    if (column.render) {
      return column.render(row, index);
    }
    const value = (row as Record<string, unknown>)[column.key];
    if (value === null || value === undefined) return '-';
    return String(value);
  };

  const renderColumnHeader = (column: Column<T>) => {
    const sortKey = column.sortKey;
    const sortable = sortKey != null && onSortChange != null;
    const isActive = Boolean(
      sortable && sortConfig && sortKey != null && sortConfig.key === sortKey,
    );

    let sortIcon = faSort;
    let iconClass = styles.sortIcon;
    if (isActive && sortConfig) {
      sortIcon = sortConfig.direction === 'desc' ? faSortDown : faSortUp;
      iconClass = clsx(styles.sortIcon, styles.sortIconActive);
    }

    if (!sortable) {
      return column.header;
    }

    return (
      <button
        type="button"
        className={clsx(
          styles.sortableHeader,
          getSortableJustifyClass(column.align),
          getAlignClass(column.align),
        )}
        onClick={() => onSortChange(sortKey)}>
        <span className={styles.headerText}>{column.header}</span>
        <FontAwesomeIcon icon={sortIcon} className={iconClass} aria-hidden />
      </button>
    );
  };

  if (error) {
    return (
      <div className={clsx(styles.container, className)}>
        <p className={styles.errorMessage}>{error}</p>
      </div>
    );
  }

  return (
    <div className={clsx(styles.container, className)}>
      {hasToolbar && (
        <div className={styles.toolbar}>
          <div className={styles.searchWrapper}>
            <Input
              type="text"
              value={search ?? ''}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              onChange={e => onSearchChange!(e.target.value)}
              placeholder={searchPlaceholder}
              icon={<FontAwesomeIcon icon={faSearch} className={formFieldStyles.fieldIcon} />}
            />
          </div>
          {filterSlot}
        </div>
      )}
      <div
        className={styles.scrollContainer}
        style={resolvedMaxHeight ? { maxHeight: resolvedMaxHeight } : undefined}>
        <table className={styles.table}>
          <thead className={maxHeight ? styles.stickyHeader : undefined}>
            <tr className={styles.headerRow}>
              {selectable && (
                <th className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={el => {
                      if (el) el.indeterminate = isSomeSelected;
                    }}
                    onChange={handleSelectAll}
                    className={styles.checkbox}
                  />
                </th>
              )}
              {columns.map(column => {
                const colSortKey = column.sortKey;
                const colSortable = colSortKey != null && onSortChange != null;
                const colActive =
                  colSortable && sortConfig && colSortKey != null && sortConfig.key === colSortKey;
                const ariaSortValue =
                  colActive && sortConfig
                    ? sortConfig.direction === 'asc'
                      ? ('ascending' as const)
                      : ('descending' as const)
                    : undefined;

                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={clsx(styles.headerCell, getAlignClass(column.align), column.width)}
                    aria-sort={ariaSortValue}>
                    {renderColumnHeader(column)}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className={styles.emptyState}>
                  <div className={styles.loadingContainer}>
                    <Spinner size="md" />
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className={styles.emptyState}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowKey = keyExtractor(row);
                const isSelected = selected.includes(rowKey);

                return (
                  <tr
                    key={rowKey}
                    onClick={() => onRowClick?.(row)}
                    className={getRowStyles(!!onRowClick, isSelected)}>
                    {selectable && (
                      <td className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={e => {
                            e.stopPropagation();
                            handleSelectRow(rowKey);
                          }}
                          onClick={e => e.stopPropagation()}
                          className={styles.checkbox}
                        />
                      </td>
                    )}
                    {columns.map(column => (
                      <td
                        key={column.key}
                        className={clsx(
                          styles.bodyCell,
                          getAlignClass(column.align),
                          column.width,
                        )}>
                        {getCellValue(row, column, index)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export type { Column, TableProps, SortConfig } from './types';
export default Table;
